import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Check, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CHOICES_1 = ["Very easy", "Somewhat easy", "Not easy"];
const CHOICES_3 = ["Yes", "Partially", "Not really"];
const CHOICES_4 = ["Yes", "Maybe", "No"];

const RadioRow = ({ label, options, value, onChange, testId }) => (
  <fieldset className="mb-6">
    <legend className="text-lg font-medium text-ink mb-3">{label}</legend>
    <div className="flex flex-wrap gap-3" data-testid={testId}>
      {options.map((opt) => (
        <button
          key={opt}
          type="button"
          onClick={() => onChange(opt)}
          aria-pressed={value === opt}
          data-testid={`${testId}-${opt.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
          className={`rounded-full px-5 py-3 text-base font-medium border-2 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-sage ${
            value === opt ? "bg-sage text-white border-sage" : "border-border text-ink hover:border-sage"
          }`}
        >
          {opt}
        </button>
      ))}
    </div>
  </fieldset>
);

const TrialFeedback = ({ trialToken }) => {
  const [form, setForm] = useState({
    staff_ease: "",
    resident_response: "",
    abilities_fit: "",
    would_recommend: "",
    testimonial: "",
    publish_permission: false,
    interested_in_purchase: false,
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.staff_ease || !form.abilities_fit || !form.would_recommend || !form.resident_response.trim()) {
      toast.error("Please answer questions 1–4 before sending.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/feedback`, {
        trial_token: trialToken || null,
        staff_ease: form.staff_ease,
        resident_response: form.resident_response,
        abilities_fit: form.abilities_fit,
        would_recommend: form.would_recommend,
        testimonial: form.testimonial || null,
        publish_permission: form.publish_permission,
        interested_in_purchase: form.interested_in_purchase,
      });
      setSent(true);
      toast.success("Thank you — your feedback has been received.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div data-testid="feedback-success" className="text-center py-8">
        <div className="h-16 w-16 rounded-full bg-sage-light flex items-center justify-center mx-auto mb-5">
          <Check className="h-8 w-8 text-sage" aria-hidden="true" />
        </div>
        <h3 className="font-heading font-bold text-2xl text-ink mb-2">Thank you for your honesty.</h3>
        <p className="text-lg text-ink/75">Your feedback helps us serve communities better.</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="feedback-form" className="text-left">
      <p className="text-base text-ink/70 mb-8">
        We are asking for honest feedback — not positive reviews. Whatever happened, we want to hear it.
      </p>
      <RadioRow
        label="1. Was UNSTUCK easy for your staff to use?"
        options={CHOICES_1}
        value={form.staff_ease}
        onChange={(v) => setForm((f) => ({ ...f, staff_ease: v }))}
        testId="feedback-q1"
      />
      <div className="mb-6">
        <Label htmlFor="fb-q2" className="text-lg font-medium text-ink mb-3 block">2. How did your residents respond to the activities?</Label>
        <Textarea
          id="fb-q2"
          data-testid="feedback-q2"
          rows={3}
          value={form.resident_response}
          onChange={(e) => setForm((f) => ({ ...f, resident_response: e.target.value }))}
          className="bg-white border-2 border-border rounded-xl px-4 py-4 text-lg focus:ring-2 focus:ring-sage focus:border-transparent outline-none transition-all"
        />
      </div>
      <RadioRow
        label="3. Were the activities appropriate for residents with different abilities?"
        options={CHOICES_3}
        value={form.abilities_fit}
        onChange={(v) => setForm((f) => ({ ...f, abilities_fit: v }))}
        testId="feedback-q3"
      />
      <RadioRow
        label="4. Would you recommend UNSTUCK to another senior community?"
        options={CHOICES_4}
        value={form.would_recommend}
        onChange={(v) => setForm((f) => ({ ...f, would_recommend: v }))}
        testId="feedback-q4"
      />
      <div className="mb-6">
        <Label htmlFor="fb-q5" className="text-lg font-medium text-ink mb-3 block">5. Would you like to share a written testimonial? (optional)</Label>
        <Textarea
          id="fb-q5"
          data-testid="feedback-q5"
          rows={4}
          value={form.testimonial}
          onChange={(e) => setForm((f) => ({ ...f, testimonial: e.target.value }))}
          className="bg-white border-2 border-border rounded-xl px-4 py-4 text-lg focus:ring-2 focus:ring-sage focus:border-transparent outline-none transition-all"
        />
      </div>
      <label className="flex items-start gap-3 mb-4 cursor-pointer">
        <input
          type="checkbox"
          checked={form.publish_permission}
          onChange={(e) => setForm((f) => ({ ...f, publish_permission: e.target.checked }))}
          data-testid="feedback-publish-permission"
          className="mt-1.5 h-5 w-5 accent-[#4A5D4E]"
        />
        <span className="text-base text-ink/80">
          You have my permission to publish my testimonial and facility name for marketing purposes.
        </span>
      </label>
      <label className="flex items-start gap-3 mb-8 cursor-pointer">
        <input
          type="checkbox"
          checked={form.interested_in_purchase}
          onChange={(e) => setForm((f) => ({ ...f, interested_in_purchase: e.target.checked }))}
          data-testid="feedback-purchase-interest"
          className="mt-1.5 h-5 w-5 accent-[#4A5D4E]"
        />
        <span className="text-base text-ink/80">
          We are interested in purchasing the complete UNSTUCK Seniors Program — please follow up.
        </span>
      </label>
      <Button
        type="submit"
        disabled={sending}
        data-testid="feedback-submit-button"
        className="w-full bg-sage text-white hover:bg-sage-dark rounded-full px-8 py-4 h-auto text-lg font-medium transition-all duration-300"
      >
        {sending && <Loader2 className="h-5 w-5 animate-spin mr-2" aria-hidden="true" />}
        Send Feedback
      </Button>
    </form>
  );
};

export default TrialFeedback;
